import { createApi } from "@reduxjs/toolkit/query/react";
import RtkBaseQueryWithReauth from "./RtkBaseQueryWithReauth";
import ApiPath from "./ConstantApi";
import Types from "../Helpers/Interfaces/ProductsType";
import Config from "../Config";

// Create the API slice
export const ProductService = createApi({
  reducerPath: "Products",
  baseQuery: RtkBaseQueryWithReauth(Config.BASE_URL),
  tagTypes: ["Products", "Wishlist"],
  keepUnusedDataFor: 300,
  endpoints: (builder) => ({
    getProducts: builder.query<
      Types.ProductListResponse,
      Types.ProductListParams
    >({
      query: (params) => ({
        url: `${ApiPath.getAllProducts}?page=${params.page}&pageSize=${params.pageSize}`,
        method: "POST",
        body: {
          filters: [
            { key: "status", value: "active" },
            { key: "isDelete", value: false },
          ],
          sortBy: "createdAt",
          sortOrder: "desc",
          page: params.page,
          pageSize: params.pageSize,
        },
      }),
      providesTags: ["Products"],
    }),
    searchProducts: builder.query<Types.SearchResponse, string>({
      query: (searchString) => ({
        url: `${ApiPath.searchProducts}${searchString}`,
        method: "GET",
      }),
    }),

    getProductDetails: builder.query<Types.ProductDetails, string>({
      query: (id) => ({
        url: `${ApiPath.getProductsDetails}/${id}`,
        method: "GET",
      }),
      transformResponse: (response: { data: Types.ProductDetails }) =>
        response.data,
      providesTags: ["Products"],
    }),

    getRatingAndReviewByProduct: builder.query<Types.ProductDetails, string>({
      query: (id) => ({
        url: `${ApiPath.ratingAndReviewByProductId}/${id}`,
        method: "GET",
      }),
      transformResponse: (response: { data: Types.ProductDetails }) =>
        response.data,
      providesTags: ["Products"],
    }),

    getPopularInCountryProduct: builder.query<Types.SearchResponse, string>({
      query: (searchString) => ({
        url: `${ApiPath.searchProducts}${searchString}`,
        method: "GET",
      }),
    }),

    addWishlist: builder.mutation<
      Types.SearchResponse,
      { userId: string; items: any[] }
    >({
      query: (payload) => ({
        url: `${ApiPath.addWishlist}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Wishlist"],
    }),

    updateWishlist: builder.mutation<
      Types.SearchResponse,
      {
        userId: string;
        productId: string;
        productDenominationId: string;
        updates: { [key: string]: any };
      }
    >({
      query: (payload) => ({
        url: `${ApiPath.updateWishlist}`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Wishlist"],
    }),

    removeWishlistItem: builder.mutation<Types.SearchResponse, string>({
      query: (id) => ({
        url: `${ApiPath.removeWishlistItem}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Wishlist"],
    }),

    removeAllWishlistItems: builder.mutation<Types.SearchResponse, string>({
      query: () => ({
        url: `${ApiPath.removeAllWishlistItems}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Wishlist"],
    }),

    getWishlist: builder.query<Types.WishlistResponseModel, Types.Pagination>({
      query: (params) => ({
        url: `${ApiPath.getWishlist}?page=${params.page}&pageSize=${params.pageSize}`,
      }),
      providesTags: ["Wishlist"],
    }),

    checkProductAreaInWishlist: builder.query<
      Types.WishlistCheckResponseModel,
      string
    >({
      query: (productId) => ({
        url: `${ApiPath.checkProductAreaInWishlist}/${productId}`,
      }),
      // Same tag the add/remove mutations invalidate, so the icon refreshes
      // straight after the customer wishlists or un-wishlists the product.
      providesTags: ["Wishlist"],
    }),

    checkWishlistProducts: builder.mutation<
      Types.SearchResponse,
      { productId: string[] }
    >({
      query: (payload) => ({
        url: `${ApiPath.checkWishlistProducts}`,
        method: "POST",
        body: payload,
      }),
    }),

    getProductEstimation: builder.mutation<
      any,
      { productId: string; productDenominationSku: string; price: number }
    >({
      query: (payload) => ({
        url: `${ApiPath.getProductEstimation}`,
        method: "POST",
        body: payload,
      }),
    }),

    // Get customer-facing price with discount applied based on payment method
    getCustomerPrice: builder.mutation<
      {
        status: string;
        data: {
          faceValue: number;
          onlinePrice: number;
          bankTransferPrice: number;
          onlineDiscount: number;
          bankTransferDiscount: number;
          taxAmount: number;
          taxRate: number;
          selectedPaymentMethod: string;
          sellingPrice: number;
          priceWithTax: number;
          discountApplied: boolean;
          discountType: 'percentage' | 'flat';
          discountValue: number;
          onlineDiscountValue: number;
          bankTransferDiscountValue: number;
        };
      },
      {
        productId: string;
        denominationSku: string;
        price?: number;
        paymentMethod?: 'Online' | 'BankTransfer';
      }
    >({
      query: (payload) => ({
        url: '/product/customer-price',
        method: 'POST',
        body: payload,
      }),
    }),

    customerLogout: builder.query<any, any>({
      query: (refreshToken) => ({
        url: `${ApiPath.customerLogout}?refreshToken=${refreshToken}`,
        method: "GET", // or POST if your API expects it
      }),
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useLazyGetProductDetailsQuery,
  useLazySearchProductsQuery,
  useLazyGetRatingAndReviewByProductQuery,
  useLazyGetPopularInCountryProductQuery,
  useGetPopularInCountryProductQuery,
  useLazyCustomerLogoutQuery,
  useAddWishlistMutation,
  useUpdateWishlistMutation,
  useLazyGetWishlistQuery,
  useRemoveWishlistItemMutation,
  useRemoveAllWishlistItemsMutation,
  useLazyCheckProductAreaInWishlistQuery,
  useCheckWishlistProductsMutation,
  useGetProductEstimationMutation,
  useGetCustomerPriceMutation,
} = ProductService;
